import { atom, PrimitiveAtom, useAtom, useSetAtom } from 'jotai';
import { v7 as idv7 } from 'uuid';
export type FileStoreStatus =
  | 'idle'
  | 'startingUpload'
  | 'uploading'
  | 'error'
  | 'complete';
export type QueueStoreStatus = 'idle' | 'uploading' | 'error' | 'complete';
export type FileStore = {
  file: File;
  progress: number;
  size: number;
  error?: string;
  errorType?: string;
  responseCode?: number;
  fileName?: string;
  fileType?: string;
  status: FileStoreStatus;
  retries: number;
  id: string;
  idx: number;
};

export const fileToStore = (file: File): FileStore => {
  return {
    file,
    progress: 0,
    size: file.size,
    fileName: file.name,
    fileType: file.type,
    status: 'idle',
    retries: 0,
    id: idv7(),
    idx: -1,
  };
};
export type QueueStoreAtomType = {
  queue: PrimitiveAtom<FileStore>[];
  add: (files: File[]) => void;
  remove: (fileAtom: PrimitiveAtom<FileStore>) => void;
  activeUploadsCount: number;
  activeUploadsIds: number[];
  startUpload: () => void;
  stopAllUploads: () => void;
  startUploads: () => void;
  onUploadComplete: (id: string) => void;
  onUploadError: (id: string, error: string, responseCode: number) => void;
  indexFiles: () => void;
};

/**
 * queue store - private do not export
 * */
const queueAtom = atom<PrimitiveAtom<FileStore>[]>([]);

export const clearQueueAtom = atom(null, (get, set) => {
  set(queueAtom, []);
  set(queueStatusAtom, 'idle');
});
/**
 * actiiv
 */
export const queueAtomUploadCount = atom(0);
// export const queueFilesList = atom<FileStore[]>(get => {
//     return get(queueAtom).map((fileAtom)=>get(fileAtom));
// });
export const queueFileAtomsList = atom((get) => get(queueAtom));
export const queueStatusAtom = atom<QueueStoreStatus>('idle');
export const queueSetFileProgress = atom(
  null,
  (get, set, { id, progress }: { id: string; progress: number }) => {
    const queue = get(queueAtom);
    for (let i = 0; i < queue.length; i++) {
      const fileAtom = queue[i];
      const file = get(fileAtom);
      if (file.id === id) {
        set(fileAtom, (prev) => ({ ...prev, progress }));
        break;
      }
    }
  },
);

export const queueAddFilesAtom = atom(null, (get, set, files: File[]) => {
  set(queueAtom, (prev) => [
    ...prev,
    ...files.map((f) => atom<FileStore>(fileToStore(f))),
  ]);
});
export const queueStartUploadsAtom = atom(null, (get, set) => {
  set(queueStatusAtom, 'uploading');
  set(updateQueue);
});

export const queueUpdateActiveUploads = atom(null, (get, set) => {
  const queue = get(queueAtom);
  const activeUploads = queue.filter(
    (fileAtom) => get(fileAtom).status === 'uploading',
  );
  set(queueAtomUploadCount, activeUploads.length);
});

export const updateQueue = atom(null, (get, set) => {
  const queue = get(queueAtom);

  const status = get(queueStatusAtom);
  if (status === 'uploading') {
    const idle = queue.filter((fileAtom) => get(fileAtom).status === 'idle');
    if (idle.length > 0) {
      const fileAtom = idle[0];
      const fileState = get(fileAtom);
      const uploadFn = get(fileUploadFnAtom);
      set(fileAtom, (prev) => ({ ...prev, status: 'uploading' }));
      if (uploadFn) {
        uploadFn.fileUploadFn(fileState.id, fileState.file);
      }
      // set(fileAtom,((prev)=>({...prev, status: "startingUpload"})));
    } else {
      set(queueStatusAtom, 'complete');
      set(queueAtom, (prev) => {
        const activeUploads = prev.filter((fileAtom) => {
          const fileData = get(fileAtom);
          return fileData.status !== 'complete';
        });
        return activeUploads;
      });
    }
  }
});
export type FileUploadCompleteEvt = {
  id: string;
  error?: string;
  code?: number;
  errorMessage?: string;
  errorType?: string;
};
/**
 * triggered when a file upload is complete
 */
export const fileUploadCompleteAtom = atom(
  null,
  (get, set, completeEvt: FileUploadCompleteEvt) => {
    const queue = get(queueAtom);
    set(queueStatusAtom, 'uploading');
    for (let i = 0; i < queue.length; i++) {
      const fileAtom = queue[i];
      const file = get(fileAtom);
      if (file.id === completeEvt.id) {
        const status = completeEvt.code === 200 ? 'complete' : 'error';
        set(fileAtom, (prev) => ({
          ...prev,
          status: status,
          error:
            completeEvt.errorMessage ||
            completeEvt.error ||
            (completeEvt.code !== 200 ? 'Upload failed' : ''),
          errorType: completeEvt.errorType,
          retries: prev.retries + 1,
          responseCode: completeEvt.code,
        }));
        break;
      }
    }
    set(updateQueue);
  },
);

export const fileUploadFnAtom = atom<
  { fileUploadFn: (id: string, file: File) => void } | undefined
>();
export const useQueue = () => {
  const [queue, setQueue] = useAtom(queueAtom);
  const addFiles = useSetAtom(queueAddFilesAtom);

  const remove = (fileAtom: PrimitiveAtom<FileStore>) => {
    setQueue((prev) => prev.filter((atom) => atom !== fileAtom));
  };
  return { queue, addFiles, remove };
};
